/**
 * Angebot anlegen.
 *
 * Drei Schritte – Kategorie, Kunde, Zuordnung – und rechts die Live-Vorschau des
 * Datensatznamens. Beim Speichern entstehen zusätzlich der SharePoint-Ordner und
 * das Monday-Item unter demselben Namen.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { KundenModal } from '../modals/KundenModal';
import { Kundenvorschlaege, useKundensuche } from '../komponenten/Kundensuche';
import type { Angebot, Kunde, KundenTreffer } from '../typen';
import './neu.css';

interface Formular {
  kategorie: string | null;
  kundeId: number | null;
  name: string;
  adresse: string;
  ort: string;
  telefon: string;
  mail: string;
  kalkulant: string;
}

export function NeuesAngebot() {
  const navigate = useNavigate();
  const meta = useMeta();
  const { nutzer, toastZeigen } = useKora();
  const queryClient = useQueryClient();

  const [f, setF] = useState<Formular>({
    kategorie: null,
    kundeId: null,
    name: '',
    adresse: '',
    ort: '',
    telefon: '',
    mail: '',
    kalkulant: nutzer?.name ?? meta.team[0]?.name ?? '',
  });
  const [kundenModal, setKundenModal] = useState(false);
  const [fehler, setFehler] = useState('');

  const nummerAbfrage = useQuery({
    queryKey: ['naechste-angebotsnummer'],
    queryFn: () => api.hole<{ nr: number }>('/angebote/naechste-nummer'),
  });

  const kundenAbfrage = useQuery({
    queryKey: ['kunden'],
    queryFn: () => api.hole<Kunde[]>('/kunden'),
  });

  const angeboteAbfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
  });

  // Live-Suche in der Kundendatenbank – dieselbe Mechanik wie in der Kundenmaske.
  const suche = useKundensuche(f.name, Boolean(f.kundeId));

  // Dublettenprüfung über Mail, Telefon, Adresse und Name.
  const [dubletten, setDubletten] = useState<KundenTreffer[]>([]);
  useEffect(() => {
    if (f.kundeId) {
      setDubletten([]);
      return;
    }
    const zeitgeber = window.setTimeout(() => {
      void api
        .sende<KundenTreffer[]>('/kunden/dubletten', {
          id: null,
          typ: 'person',
          nachname: f.name,
          mail: f.mail,
          telefon: f.telefon,
          strasse: f.adresse,
        })
        .then(setDubletten)
        .catch(() => setDubletten([]));
    }, 400);
    return () => window.clearTimeout(zeitgeber);
  }, [f.kundeId, f.name, f.mail, f.telefon, f.adresse]);

  const nr = nummerAbfrage.data?.nr ?? 0;

  /** Nachname bzw. prägnanter Namensteil – gleiche Regel wie am Server. */
  const nachname = useMemo(() => {
    const roh = f.name.replace(/^(Ing\.|Dr\.|DI|Mag\.|Familie|Zimmerei|Bauträger)\s+/i, '').trim();
    const teile = roh.split(/\s+/).filter(Boolean);
    const letzte = teile[teile.length - 1] ?? '';
    if (/^(GmbH|e\.U\.|KG|OG|AG)$/i.test(letzte) && teile.length > 1) {
      return teile[teile.length - 2] ?? letzte;
    }
    return letzte;
  }, [f.name]);

  const vorschau = `${nr || '…'}_${f.kategorie ?? '??'}_${nachname || 'Nachname'} (${f.ort.trim() || 'Ort'})`;

  const gueltig = !!f.kategorie && !!f.name.trim() && !!f.ort.trim();

  const bestand = f.kundeId ? kundenAbfrage.data?.find((k) => k.id === f.kundeId) : null;
  const bestandAngebote = bestand
    ? (angeboteAbfrage.data ?? [])
        .filter((a) => a.kunde.id === bestand.id)
        .map((a) => a.prefix)
        .join(' + ')
    : '';

  const anlegen = useMutation({
    mutationFn: () =>
      api.sende<{ angebot: Angebot; sync: { ok: boolean; fehler: string | null } }>('/angebote', {
        kategorie: f.kategorie,
        kundeId: f.kundeId,
        name: f.name.trim(),
        adresse: f.adresse.trim(),
        ort: f.ort.trim(),
        telefon: f.telefon.trim(),
        mail: f.mail.trim(),
        kalkulant: f.kalkulant,
      }),
    onSuccess: (antwort) => {
      void queryClient.invalidateQueries();
      if (antwort.sync.ok) {
        toastZeigen('Angebot angelegt – Monday-Item und SharePoint-Ordner sind erzeugt.');
      } else {
        toastZeigen('Angebot angelegt – der Abgleich meldet ein Problem, siehe Hinweis am Angebot.');
      }
      navigate(`/angebot/${antwort.angebot.id}`);
    },
    onError: (fe) => setFehler(fe instanceof Error ? fe.message : 'Anlegen fehlgeschlagen.'),
  });

  function setzen<K extends keyof Formular>(feld: K, wert: Formular[K]) {
    setF((alt) => ({ ...alt, [feld]: wert }));
    setFehler('');
  }

  function kundeUebernehmen(k: Kunde) {
    setF((alt) => ({
      ...alt,
      kundeId: k.id,
      name: k.name,
      adresse: k.strasse,
      ort: k.ort,
      telefon: k.telefon,
      mail: k.mail,
    }));
    suche.schliessen();
  }

  return (
    <div className="neu">
      <div className="neu-spalte">
        <div>
          <button type="button" className="neu-zurueck" onClick={() => navigate('/cockpit')}>
            ← Vertriebscockpit
          </button>
          <div className="eh-kicker" style={{ marginTop: 14 }}>
            Neuanlage
          </div>
          <div className="eh-titel">Angebot anlegen</div>
        </div>

        {/* --- Schritt 1: Kategorie --- */}
        <div className="neu-karte">
          <div className="neu-schritt">
            <span className="neu-nummer">1</span>
            <span className="neu-schritt-titel">Kategorie</span>
            <span className="neu-schritt-hinweis">bestimmt Präfix und laufende Nummer</span>
          </div>
          <div className="neu-kategorien">
            {meta.kategorien.map((c) => (
              <button
                key={c.prefix}
                type="button"
                className={`neu-kategorie ${f.kategorie === c.prefix ? 'aktiv' : ''}`}
                onClick={() => setzen('kategorie', c.prefix)}
              >
                <span className="neu-kategorie-prefix">{c.prefix}</span>
                <span className="neu-kategorie-label">{c.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* --- Schritt 2: Kunde --- */}
        <div className="neu-karte">
          <div className="neu-schritt">
            <span className="neu-nummer">2</span>
            <span className="neu-schritt-titel">Kunde</span>
            <span className="neu-schritt-hinweis">
              Duplikat-Check über Mail, Telefon und Adresse
            </span>
            <button
              type="button"
              className="neu-kundenmaske"
              onClick={() => setKundenModal(true)}
            >
              Kunden neu anlegen +
            </button>
          </div>

          {bestand && (
            <div className="neu-bestand">
              <div>
                <strong>Bestandskunde verknüpft:</strong> {bestand.name}
                {bestandAngebote ? ` · ${bestandAngebote} bereits im System` : ''}
              </div>
              <button
                type="button"
                className="neu-loesen"
                onClick={() => {
                  setzen('kundeId', null);
                  suche.oeffnen();
                }}
              >
                Lösen
              </button>
            </div>
          )}

          <div className="neu-raster">
            <label className="neu-feld neu-voll ks-feld">
              <span className="neu-feldlabel">
                <span>Kundenname</span>
                <span className="neu-suchstatus">
                  {f.kundeId
                    ? 'Bestandskunde verknüpft'
                    : f.name.trim().length >= 2
                      ? 'live gesucht'
                      : 'ab 2 Zeichen wird gesucht'}
                </span>
              </span>
              {/*
                Bedienbar ohne Maus: Pfeiltasten wählen, Enter übernimmt,
                Escape blendet aus. Wer viele Kunden anlegt, tippt und drückt
                Enter – die Hand bleibt auf der Tastatur.
              */}
              <input
                className="neu-eingabe"
                value={f.name}
                placeholder="Tippen – sucht live in der Kundendatenbank"
                role="combobox"
                aria-expanded={suche.offen}
                aria-autocomplete="list"
                onChange={(e) => {
                  setF((alt) => ({ ...alt, name: e.target.value, kundeId: null }));
                  suche.oeffnen();
                  setFehler('');
                }}
                onKeyDown={(e) => suche.taste(e, kundeUebernehmen)}
              />

              <Kundenvorschlaege
                suche={f.name}
                suchstand={suche}
                onWaehlen={kundeUebernehmen}
                zusatz={(k) => {
                  const anzahl = (angeboteAbfrage.data ?? []).filter(
                    (a) => a.kunde.id === k.id,
                  ).length;
                  return (
                    <span className="ks-anzahl">
                      {anzahl === 0
                        ? 'noch kein Angebot'
                        : anzahl === 1
                          ? '1 Angebot'
                          : `${anzahl} Angebote`}
                    </span>
                  );
                }}
                leer={
                  <div className="ks-leer">
                    <span>Kein Treffer – wird als neuer Kunde angelegt.</span>
                    <button type="button" onClick={() => setKundenModal(true)}>
                      Volle Kundenmaske
                    </button>
                  </div>
                }
              />
            </label>

            <label className="neu-feld">
              <span className="neu-feldlabel">Adresse</span>
              <input
                className="neu-eingabe"
                value={f.adresse}
                placeholder="Straße und Nummer"
                onChange={(e) => setzen('adresse', e.target.value)}
              />
            </label>

            <label className="neu-feld">
              <span className="neu-feldlabel">Ort</span>
              <input
                className="neu-eingabe"
                value={f.ort}
                placeholder="z. B. Wien"
                onChange={(e) => setzen('ort', e.target.value)}
              />
            </label>

            <label className="neu-feld">
              <span className="neu-feldlabel">Telefon</span>
              <input
                className="neu-eingabe"
                value={f.telefon}
                placeholder="+43 …"
                onChange={(e) => setzen('telefon', e.target.value)}
              />
            </label>

            <label className="neu-feld">
              <span className="neu-feldlabel">E-Mail</span>
              <input
                className="neu-eingabe"
                value={f.mail}
                placeholder="name@domain.at"
                onChange={(e) => setzen('mail', e.target.value)}
              />
            </label>
          </div>

          {dubletten.length > 0 && (
            <div className="neu-dubletten">
              <div className="neu-dubletten-kopf">
                <span className="neu-dubletten-punkt" />
                Möglicher Duplikat-Treffer
              </div>
              <div className="neu-dubletten-liste">
                {dubletten.map((t) => (
                  <div className="neu-dublette" key={t.kunde.id}>
                    <div>
                      <div className="neu-dublette-name">{t.kunde.name}</div>
                      <div className="neu-dublette-zeile">
                        {t.kunde.adresse}, {t.kunde.ort} · {t.kunde.mail || '—'}
                      </div>
                      <div className="neu-dublette-grund">Übereinstimmung: {t.grund}</div>
                    </div>
                    <button
                      type="button"
                      className="neu-dublette-knopf"
                      onClick={() => kundeUebernehmen(t.kunde)}
                    >
                      Kunden verwenden
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* --- Schritt 3: Zuordnung --- */}
        <div className="neu-karte">
          <div className="neu-schritt">
            <span className="neu-nummer">3</span>
            <span className="neu-schritt-titel">Zuordnung</span>
          </div>
          <div className="neu-raster neu-raster--zwei">
            <label className="neu-feld">
              <span className="neu-feldlabel">Kalkulant / Verantwortlich</span>
              <select
                className="neu-eingabe"
                value={f.kalkulant}
                onChange={(e) => setzen('kalkulant', e.target.value)}
              >
                {meta.team.map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="neu-feld">
              <span className="neu-feldlabel">Startstatus</span>
              <input className="neu-eingabe neu-gesperrt" value="in Arbeit" readOnly />
            </label>
          </div>
        </div>
      </div>

      {/* --- Vorschau --- */}
      <div className="neu-seitenspalte">
        <div className="neu-vorschau">
          <div className="neu-vorschau-label">Vorschau Datensatzname</div>
          <div className="neu-vorschau-name">{vorschau}</div>
          <div className="neu-vorschau-raster">
            <div>
              <div className="neu-vorschau-klein">Laufende Nummer</div>
              <div className="neu-vorschau-wert">{nr || '…'}</div>
            </div>
            <div>
              <div className="neu-vorschau-klein">Präfix</div>
              <div className="neu-vorschau-wert">
                {f.kategorie
                  ? `${f.kategorie} · ${meta.kategorien.find((k) => k.prefix === f.kategorie)?.label ?? ''}`
                  : '— Kategorie wählen'}
              </div>
            </div>
            <div>
              <div className="neu-vorschau-klein">Monday-Item</div>
              <div className="neu-vorschau-text">
                {meta.integrationen.monday ? 'wird angelegt' : 'nicht konfiguriert'}
              </div>
            </div>
            <div>
              <div className="neu-vorschau-klein">SharePoint</div>
              <div className="neu-vorschau-text">
                {meta.integrationen.graph ? 'Ordner wird erzeugt' : 'nicht konfiguriert'}
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          className={`neu-speichern ${gueltig ? '' : 'gesperrt'}`}
          disabled={!gueltig || anlegen.isPending}
          onClick={() => anlegen.mutate()}
        >
          {anlegen.isPending ? 'Wird angelegt …' : 'Angebot anlegen'}
        </button>

        <div className="neu-speichern-hinweis">
          {fehler ||
            (gueltig
              ? 'Erzeugt den Datensatz, das Monday-Item und den SharePoint-Ordner unter identischem Namen.'
              : 'Kategorie, Kundenname und Ort sind Pflicht – daraus entsteht der standardisierte Name.')}
        </div>
      </div>

      {kundenModal && (
        <KundenModal
          kundeId={null}
          onSchliessen={() => setKundenModal(false)}
          onGespeichert={(k) => {
            kundeUebernehmen(k);
            setKundenModal(false);
          }}
        />
      )}
    </div>
  );
}
